import { inject, Injectable, type OnDestroy, signal } from '@angular/core';
import { LoggingService } from '@common/logging/logging.service';
import { filter, map, type Observable, Subject } from 'rxjs';
import type {
  AppointmentSyncedNotification,
  AppointmentSyncFailedNotification,
  AppointmentSyncProgressNotification,
  AppointmentSyncStartedNotification,
  CommunicationContentReadyNotification,
  EmailFailedNotification,
  EmailSentNotification,
  ListChangedNotification,
  WebSocketNotification as GeneratedWebSocketNotification,
} from 'src/proxy';

import type { AlertButton, AlertType } from '@/shared/models/';
import { ToastService } from '@/shared/services/toast.service';

import { ConfigService } from './config';

// =============================================================================
// Notification Type Constants
// =============================================================================

/**
 * All known notification type strings.
 * These match the values from Django's NotificationType enum.
 */
export const NotificationTypes = {
  APPOINTMENT_CANCELLED: 'appointment.cancelled',
  APPOINTMENT_CREATED: 'appointment.created',
  APPOINTMENT_SYNC_FAILED: 'appointment.sync_failed',
  APPOINTMENT_SYNC_PROGRESS: 'appointment.sync_progress',
  APPOINTMENT_SYNC_STARTED: 'appointment.sync_started',
  APPOINTMENT_SYNCED: 'appointment.synced',
  COMMUNICATION_CONTENT_READY: 'communication.content_ready',
  DOCTOLIB_SYNC_COMPLETED: 'doctolib.sync.completed',
  EMAIL_FAILED: 'email.failed',
  EMAIL_SENT: 'email.sent',
  LIST_CHANGED: 'list.changed',
} as const;

/** Union of all notification type strings */
export type NotificationType =
  (typeof NotificationTypes)[keyof typeof NotificationTypes];

// =============================================================================
// Type Mapping
// =============================================================================

/**
 * Maps notification type strings to their corresponding notification interfaces.
 * This enables type-safe subscriptions via the `on()` method.
 */
export interface NotificationTypeMap {
  'appointment.sync_started': AppointmentSyncStartedNotification;
  'appointment.sync_progress': AppointmentSyncProgressNotification;
  'appointment.synced': AppointmentSyncedNotification;
  'appointment.sync_failed': AppointmentSyncFailedNotification;
  'email.sent': EmailSentNotification;
  'email.failed': EmailFailedNotification;
  'communication.content_ready': CommunicationContentReadyNotification;
  'list.changed': ListChangedNotification;
}

// =============================================================================
// Toast Configuration (local interface for backward compatibility)
// =============================================================================

/**
 * Toast configuration from the backend.
 * Maps to ToastConfig on the frontend.
 */
export interface WebSocketToastConfig {
  message: string;
  toastType: AlertType;
  headline?: string;
  duration?: number;
  closable?: boolean;
  buttons?: AlertButton<unknown>[];
}

/**
 * Notification payload from the backend.
 * Use the generated `WebSocketNotification` type from proxy for typed access.
 *
 * @deprecated Prefer using the typed `on()` method with specific notification types
 */
export interface WebSocketNotification {
  type: string;
  data?: Record<string, unknown>;
  toast?: WebSocketToastConfig;
}

// Re-export the generated union type for convenience
export type { GeneratedWebSocketNotification };

@Injectable({
  providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
  readonly #configService = inject(ConfigService);
  readonly #toastService = inject(ToastService);
  readonly #logger = inject(LoggingService);

  #socket: WebSocket | null = null;
  #reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  #isIntentionalClose = false;
  #hadSuccessfulConnection = false;

  // Phase 1: Fast retries with exponential backoff + jitter
  #reconnectAttempts = 0;
  readonly #maxFastRetries = 5;
  readonly #initialRetryDelay = 1000; // 1s → 2s → 4s → 8s → 16s

  // Phase 2: Slow background polling
  #isSlowPolling = false;
  readonly #slowPollInterval = 30_000;

  /** Close code sent by Django when the user is not authenticated. */
  readonly #AUTH_REJECTED_CODE = 4001;

  // Bound handlers for browser recovery events (so we can remove them)
  readonly #onOnline = (): void => this.#handleBrowserOnline();
  readonly #onVisible = (): void => this.#handleTabVisible();

  /** Reactive connection status for the system-info page and diagnostics. */
  readonly #connectionStatus = signal<
    'disconnected' | 'connecting' | 'connected' | 'reconnecting'
  >('disconnected');
  readonly connectionStatus = this.#connectionStatus.asReadonly();

  /**
   * Subject that emits all incoming notifications.
   * Use `on()` method for filtered subscriptions.
   */
  readonly #notifications$ = new Subject<WebSocketNotification>();

  /**
   * Observable of all notifications. Prefer using `on()` for type-filtered subscriptions.
   */
  readonly notifications$ = this.#notifications$.asObservable();

  ngOnDestroy(): void {
    this.disconnect();
    this.#notifications$.complete();
    this.#stopBrowserRecoveryListeners();
  }

  /**
   * Subscribe to notifications of a specific type with full type safety.
   *
   * @param type - The notification type to subscribe to
   * @returns Observable of the typed notification
   *
   * @example
   * ```typescript
   * // Fully typed - data has AppointmentSyncProgressData type
   * this.webSocketService.on('appointment.sync_progress').subscribe(notification => {
   *   console.log(notification.data.completedSteps); // TypeScript knows this exists
   * });
   * ```
   */
  on<T extends keyof NotificationTypeMap>(
    type: T
  ): Observable<NotificationTypeMap[T]>;
  /**
   * Subscribe to notifications matching a pattern.
   * Supports exact match, prefix match with wildcard, or all notifications.
   *
   * @param typePattern - Pattern to match:
   *   - Exact: 'appointment.synced' matches only 'appointment.synced'
   *   - Prefix: 'appointment.*' matches all appointment notifications
   *   - All: '*' matches all notifications
   * @returns Observable of untyped notifications (use typed overload for type safety)
   *
   * @example
   * ```typescript
   * // Pattern matching - returns untyped notification
   * this.webSocketService.on('appointment.*').subscribe(notification => {
   *   // notification.data is Record<string, unknown>
   *   this.refreshCalendar();
   * });
   * ```
   */
  on(typePattern: string): Observable<WebSocketNotification>;
  // Implementation
  on(
    typePattern: string
  ):
    | Observable<NotificationTypeMap[keyof NotificationTypeMap]>
    | Observable<WebSocketNotification> {
    return this.#notifications$.pipe(
      filter(notification =>
        this.#matchesPattern(notification.type, typePattern)
      ),
      // Cast through unknown to satisfy TypeScript when narrowing from generic to specific type
      map(
        notification =>
          notification as unknown as NotificationTypeMap[keyof NotificationTypeMap]
      )
    );
  }

  #matchesPattern(type: string, pattern: string): boolean {
    if (pattern === '*') {
      return true;
    }
    if (pattern.endsWith('.*')) {
      const prefix = pattern.slice(0, -2);
      return type === prefix || type.startsWith(prefix + '.');
    }
    return type === pattern;
  }

  /**
   * Initialize WebSocket connection. Safe to call multiple times -
   * subsequent calls are ignored if already connected or connecting.
   */
  connect(): void {
    if (
      this.#socket?.readyState === WebSocket.OPEN ||
      this.#socket?.readyState === WebSocket.CONNECTING
    ) {
      this.#logger.debug('[WebSocket] Already connected or connecting');
      return;
    }

    if (this.#socket?.readyState === WebSocket.CLOSING) {
      this.#logger.debug(
        '[WebSocket] Socket is closing, will reconnect via onclose handler'
      );
      return;
    }

    this.#cancelPendingReconnect();
    this.#reconnectAttempts = 0;
    this.#isSlowPolling = false;
    this.#isIntentionalClose = false;
    this.#hadSuccessfulConnection = false;
    this.#startBrowserRecoveryListeners();

    this.#createSocket();
  }

  disconnect(): void {
    this.#logger.debug('[WebSocket] Disconnecting');
    this.#isIntentionalClose = true;
    this.#cancelPendingReconnect();
    this.#stopBrowserRecoveryListeners();
    this.#closeExistingSocket();

    this.#reconnectAttempts = 0;
    this.#isSlowPolling = false;
    this.#connectionStatus.set('disconnected');
  }

  #createSocket(): void {
    this.#closeExistingSocket();

    const apiUrl = this.#configService.readConfig().API_URL;
    if (!apiUrl) {
      this.#logger.error('[WebSocket] Cannot connect: API_URL not configured');
      return;
    }

    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const wsUrl = apiUrl
      .replace(/^https?:/, wsProtocol + ':')
      .replace(/^\/\//, `${wsProtocol}://`);
    const fullUrl = `${wsUrl}/ws/notifications/`;

    this.#logger.debug('[WebSocket] Connecting to:', fullUrl);
    this.#connectionStatus.set('connecting');

    try {
      this.#socket = new WebSocket(fullUrl);
      this.#setupEventHandlers();
    } catch (error) {
      this.#logger.error('[WebSocket] Failed to create WebSocket:', error);
      this.#scheduleReconnect();
    }
  }

  /** Detach handlers and close any existing socket to prevent orphaned connections. */
  #closeExistingSocket(): void {
    if (!this.#socket) return;

    this.#socket.onopen = null;
    this.#socket.onclose = null;
    this.#socket.onerror = null;
    this.#socket.onmessage = null;

    if (
      this.#socket.readyState === WebSocket.OPEN ||
      this.#socket.readyState === WebSocket.CONNECTING
    ) {
      this.#socket.close();
    }

    this.#socket = null;
  }

  #cancelPendingReconnect(): void {
    if (this.#reconnectTimeout) {
      clearTimeout(this.#reconnectTimeout);
      this.#reconnectTimeout = null;
    }
  }

  #setupEventHandlers(): void {
    if (!this.#socket) return;

    this.#socket.onopen = (): void => {
      this.#logger.info('[WebSocket] Connected successfully');
      this.#reconnectAttempts = 0;
      this.#isSlowPolling = false;
      this.#isIntentionalClose = false;
      this.#hadSuccessfulConnection = true;
      this.#connectionStatus.set('connected');
    };

    this.#socket.onclose = (event: CloseEvent): void => {
      this.#logger.info(
        '[WebSocket] Connection closed:',
        `code=${event.code}`,
        `reason=${event.reason}`,
        `wasClean=${event.wasClean}`
      );

      if (event.code === this.#AUTH_REJECTED_CODE) {
        this.#logger.warn(
          '[WebSocket] Server rejected connection (not authenticated), stopping reconnect'
        );
        this.disconnect();
        return;
      }

      if (!this.#isIntentionalClose) {
        this.#scheduleReconnect();
      } else {
        this.#connectionStatus.set('disconnected');
      }
    };

    this.#socket.onerror = (error: Event): void => {
      this.#logger.error('[WebSocket] Error:', error);
    };

    this.#socket.onmessage = (event: MessageEvent): void => {
      this.#handleMessage(event);
    };
  }

  #handleMessage(event: MessageEvent): void {
    try {
      const notification = JSON.parse(event.data) as WebSocketNotification;
      this.#logger.debug('[WebSocket] Received notification:', notification);

      // Handle pong response (for ping/pong health checks)
      if (notification.type === 'pong') {
        this.#logger.debug('[WebSocket] Received pong');
        return;
      }

      // Emit to subscribers
      this.#notifications$.next(notification);

      // Show toast if toast config is present
      if (notification.toast) {
        this.#logger.debug('[WebSocket] Showing toast:', notification.toast);
        const toast = notification.toast;
        this.#toastService.show(toast.message, {
          buttons: toast.buttons,
          closable: toast.closable ?? true,
          duration: toast.duration ?? 5000,
          headline: toast.headline,
          type: toast.toastType,
        });
      }
    } catch (error) {
      this.#logger.error('[WebSocket] Failed to parse message:', error);
    }
  }

  /**
   * Two-phase reconnection strategy:
   * 1. Fast retries with exponential backoff + jitter (transient failures)
   * 2. Slow background polling every 30s (longer outages)
   *
   * Browser events (online, tab visible) can trigger immediate retries
   * at any point during either phase.
   */
  #scheduleReconnect(): void {
    this.#connectionStatus.set('reconnecting');

    // Phase 1: Fast retries with exponential backoff
    if (this.#reconnectAttempts < this.#maxFastRetries) {
      this.#reconnectAttempts++;
      const baseDelay =
        this.#initialRetryDelay * Math.pow(2, this.#reconnectAttempts - 1);
      const jitter = baseDelay * 0.25 * (Math.random() * 2 - 1);
      const delay = Math.round(baseDelay + jitter);

      this.#logger.info(
        `[WebSocket] Reconnect attempt ${this.#reconnectAttempts}/${this.#maxFastRetries} in ${delay}ms`
      );

      this.#reconnectTimeout = setTimeout(() => this.#createSocket(), delay);
      return;
    }

    if (!this.#hadSuccessfulConnection) {
      this.#logger.warn(
        '[WebSocket] Fast retries exhausted without a successful connection, giving up'
      );
      this.disconnect();
      return;
    }

    // Phase 2: Slow background polling (only after a previously successful connection)
    if (!this.#isSlowPolling) {
      this.#isSlowPolling = true;
      this.#logger.warn(
        `[WebSocket] Fast retries exhausted, polling every ${this.#slowPollInterval / 1000}s until server returns`
      );
    }

    this.#reconnectTimeout = setTimeout(
      () => this.#createSocket(),
      this.#slowPollInterval
    );
  }

  // =========================================================================
  // Browser recovery: reconnect immediately when conditions improve
  // =========================================================================

  #startBrowserRecoveryListeners(): void {
    window.addEventListener('online', this.#onOnline);
    document.addEventListener('visibilitychange', this.#onVisible);
  }

  #stopBrowserRecoveryListeners(): void {
    window.removeEventListener('online', this.#onOnline);
    document.removeEventListener('visibilitychange', this.#onVisible);
  }

  #handleBrowserOnline(): void {
    if (
      this.#hadSuccessfulConnection &&
      !this.#isIntentionalClose &&
      this.#socket?.readyState !== WebSocket.OPEN
    ) {
      this.#logger.info(
        '[WebSocket] Browser went online, attempting immediate reconnect'
      );
      this.#cancelPendingReconnect();
      this.#reconnectAttempts = 0;
      this.#isSlowPolling = false;
      this.#createSocket();
    }
  }

  #handleTabVisible(): void {
    if (
      document.visibilityState === 'visible' &&
      this.#hadSuccessfulConnection &&
      !this.#isIntentionalClose &&
      this.#socket?.readyState !== WebSocket.OPEN
    ) {
      this.#logger.info(
        '[WebSocket] Tab became visible, attempting immediate reconnect'
      );
      this.#cancelPendingReconnect();
      this.#reconnectAttempts = 0;
      this.#isSlowPolling = false;
      this.#createSocket();
    }
  }

  /**
   * Send a ping to check connection health
   */
  ping(): void {
    if (this.#socket?.readyState === WebSocket.OPEN) {
      this.#logger.debug('[WebSocket] Sending ping');
      this.#socket.send(JSON.stringify({ type: 'ping' }));
    }
  }
}
